import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Link, useTranslation, useSelector, useDispatch } from '@spcc/core'
import { getNextLanguage, isRtlLanguage } from '@spcc/portal-shared'
import { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Label,
  ArrowLeft,
  Globe,
  CheckCircle2,
  Eye,
  EyeOff,
  Alert,
  AlertDescription,
  AlertTriangle,
} from '@spcc/uikit'
import {
  authActions,
  selectIsAuthenticated,
  selectIsPasswordResetLoadingState,
  selectPasswordResetStatus,
  selectPasswordResetMessage,
} from '@spcc/domains/auth'

type ResetPasswordSearch = {
  code?: string
}

const RESET_PASSWORD_TOKEN_KEYS = ['code', 'token', 'resetPasswordToken'] as const

function normalizeResetCodeCandidate(value: unknown): string {
  if (typeof value !== 'string') {
    return ''
  }

  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return ''
  }

  const withoutHash = trimmedValue.split('#')[0]?.trim() ?? ''
  if (!withoutHash) {
    return ''
  }

  const queryStartIndex = withoutHash.indexOf('?')
  const queryString = queryStartIndex >= 0 ? withoutHash.slice(queryStartIndex + 1) : withoutHash
  const searchParams = new URLSearchParams(queryString)

  for (const key of RESET_PASSWORD_TOKEN_KEYS) {
    const paramValue = searchParams.get(key)
    if (paramValue) {
      return paramValue.trim()
    }
  }

  return withoutHash
}

function resolveResetPasswordCode(search: Record<string, unknown>): string {
  for (const key of RESET_PASSWORD_TOKEN_KEYS) {
    const candidate = normalizeResetCodeCandidate(search[key])
    if (candidate) {
      return candidate
    }
  }

  return ''
}

function validateResetPasswordSearch(search: Record<string, unknown>): ResetPasswordSearch {
  const code = resolveResetPasswordCode(search)

  if (!code) {
    return {}
  }

  return { code }
}

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: validateResetPasswordSearch,
})

function canSubmitResetPassword(code: string, password: string, passwordConfirmation: string): boolean {
  const trimmedCode = code.trim()
  if (!trimmedCode) return false
  if (!password || !passwordConfirmation) return false
  if (password !== passwordConfirmation) return false

  return true
}

function getPasswordMismatchError(password: string, passwordConfirmation: string): string | null {
  if (!password || !passwordConfirmation) {
    return null
  }

  if (password === passwordConfirmation) {
    return null
  }

  return 'Passwords do not match'
}

function getResetPasswordError(status: string, message: string | null): string | null {
  if (status !== 'error') {
    return null
  }

  return message || 'Password reset failed'
}

function getMissingCodeError(code: string): string | null {
  if (code.trim()) {
    return null
  }

  return 'Reset link is invalid or missing code. Please request a new reset email.'
}

function buildResetPasswordPayload(code: string, password: string, passwordConfirmation: string) {
  return {
    code: code.trim(),
    password,
    passwordConfirmation,
  }
}

function ResetPasswordPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const search = Route.useSearch()
  const code = search.code ?? ''

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsPasswordResetLoadingState)
  const passwordResetStatus = useSelector(selectPasswordResetStatus)
  const passwordResetMessage = useSelector(selectPasswordResetMessage)

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const isRTL = isRtlLanguage(i18n.language)
  const isSubmitted = passwordResetStatus === 'success' && !isAuthenticated
  const missingCodeError = getMissingCodeError(code)
  const passwordMismatchError = getPasswordMismatchError(password, passwordConfirmation)
  const resetError = getResetPasswordError(passwordResetStatus, passwordResetMessage)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    navigate({ to: '/' })
  }, [isAuthenticated, navigate])

  useEffect(() => {
    dispatch(authActions.resetPasswordReset())

    return () => {
      dispatch(authActions.resetPasswordReset())
    }
  }, [dispatch])

  const toggleLanguage = () => {
    const newLang = getNextLanguage(i18n.language)
    i18n.changeLanguage(newLang)
    document.documentElement.dir = isRtlLanguage(newLang) ? 'rtl' : 'ltr'
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (!canSubmitResetPassword(code, password, passwordConfirmation)) {
      return
    }

    dispatch(authActions.resetPassword(buildResetPasswordPayload(code, password, passwordConfirmation)))
  }

  const handleStartOver = () => {
    dispatch(authActions.resetPasswordReset())
    setPassword('')
    setPasswordConfirmation('')
  }

  return (
    <div className="safe-top relative min-h-screen flex items-center justify-center overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="absolute top-[calc(env(safe-area-inset-top)+1rem)] end-4 glass"
        onClick={toggleLanguage}
      >
        <Globe className="h-5 w-5" />
      </Button>

      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <span className="text-2xl font-bold text-primary">SPCC</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('auth.resetPassword')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('auth.resetPasswordDescription')}
          </p>
        </div>

        <Card className="glass-card border-white/10">
          {!isSubmitted ? (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">{t('auth.resetPassword')}</CardTitle>
                <CardDescription>
                  {t('auth.resetPasswordDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {missingCodeError && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{missingCodeError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.newPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder={t('auth.passwordPlaceholder')}
                        required
                        className="bg-background/50 pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword((current) => !current)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="passwordConfirmation">{t('auth.confirmPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="passwordConfirmation"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordConfirmation}
                        onChange={(event) => setPasswordConfirmation(event.target.value)}
                        placeholder={t('auth.confirmPasswordPlaceholder')}
                        required
                        className="bg-background/50 pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword((current) => !current)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {passwordMismatchError && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{passwordMismatchError}</AlertDescription>
                    </Alert>
                  )}

                  {resetError && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{resetError}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading || !canSubmitResetPassword(code, password, passwordConfirmation)}
                  >
                    {isLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      t('auth.resetPassword')
                    )}
                  </Button>
                </form>

                <div className="mt-6 flex justify-between">
                  <Link
                    to="/forgot-password"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 rtl:rotate-180" />
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <CardTitle className="text-xl text-center">{t('auth.passwordResetSuccessful')}</CardTitle>
                <CardDescription className="text-center">
                  {passwordResetMessage || t('auth.passwordResetSuccessMessage')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full" onClick={handleStartOver}>
                  {t('auth.tryAgain')}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 rtl:rotate-180" />
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
